import type { Edulead } from 'edulead';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { allPages, toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { usePermission } from '../../hooks/usePermission';
import { hasBeaconEdits, useNavigatorStore } from '../../zustand/useNavigatorStore';
import { beaconFillColor, DEFAULT_BEACON_FILL } from './beacon-colors';
import { BeaconDetail } from './BeaconDetail';
import { BeaconList } from './BeaconList';
import './NavigatorPanel.scss';
import './NavigatorBeaconsPanel.scss';

/** The URL's query parameters: the selected beacons' ids (comma separated), and whether the one
 * selected beacon's form is open. */
const BEACON_PARAM = 'beacon';
const EDIT_PARAM = 'edit';

/**
 * The Navigator's right panel in Beacons mode: the team's beacons, and one beacon's form. A click
 * on a beacon selects it (?beacon=<id>) and the map shows its institutions; a Cmd/Ctrl+click adds
 * it to the selection or takes it out (?beacon=<id>,<id>), the map showing them all. A beacon
 * selected alone is the one the map edits, for those who may change it: the edits are staged,
 * with Save and Discard under the list, until saved. Its edit button opens it (&edit=1), sliding
 * the list out to the left and the form in; Back slides them back, the beacon still selected.
 */
export function NavigatorBeaconsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [beacons, setBeacons] = useState<Edulead.EduleadV4Beacon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  // The beacon shown in the form; kept while it slides out, so the form does not empty mid-slide.
  const [shownId, setShownId] = useState<string | null>(null);
  // The selection whose members the map shows (its ids joined), once read.
  const [shownOnMapKey, setShownOnMapKey] = useState<string | null>(null);
  const setHighlighted = useNavigatorStore((state) => state.setHighlightedInstitutionIds);
  const setHighlightFills = useNavigatorStore((state) => state.setHighlightFills);
  const setBeaconFill = useNavigatorStore((state) => state.setBeaconFill);
  const setBeaconSavedIds = useNavigatorStore((state) => state.setBeaconSavedIds);
  const rememberNames = useNavigatorStore((state) => state.rememberInstitutionNames);
  const setEditingBeaconId = useNavigatorStore((state) => state.setEditingBeaconId);
  const editingBeaconId = useNavigatorStore((state) => state.editingBeaconId);
  const savedIds = useNavigatorStore((state) => state.beaconSavedIds);
  const isDirty = useNavigatorStore(hasBeaconEdits);
  const undo = useNavigatorStore((state) => state.undoBeaconEdit);
  const redo = useNavigatorStore((state) => state.redoBeaconEdit);
  const { hasPermission } = usePermission();
  const canManage = hasPermission('beacons:manage');
  // Each beacon's institutions (id and name) once read, so opening a beacon again reads nothing.
  const beaconMembers = useRef(new Map<string, { id: string; name: string }[]>());

  // The URL's beacons, those the list has (a deleted beacon's link drops it); open only when one.
  const param = searchParams.get(BEACON_PARAM) ?? '';
  const selectedIds = param
    .split(',')
    .filter((id, i, all) => id && all.indexOf(id) === i && beacons.some((b) => b.id === id));
  const selectedKey = selectedIds.join(',');
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const openedId = selectedId && searchParams.get(EDIT_PARAM) === '1' ? selectedId : null;

  useEffect(() => {
    loadBeacons();
  }, []);

  useEffect(() => {
    if (openedId) setShownId(openedId);
  }, [openedId]);

  // A beacon selected alone is the one the map edits, once the map shows its members (before, a
  // click would not know which are in), and for those who may change it.
  const isEditable = canManage && selectedId !== null && shownOnMapKey === selectedId;
  useEffect(() => {
    setEditingBeaconId(isEditable ? selectedId : null);
  }, [isEditable, selectedId]);

  // What the API holds of the edited beacon, as read or as a save left it: the cache and the
  // list's count take it.
  useEffect(() => {
    if (!isEditable || !selectedId || !savedIds) return;
    const { institutionNames } = useNavigatorStore.getState();
    beaconMembers.current.set(
      selectedId,
      savedIds.map((i) => ({ id: i, name: institutionNames[i] ?? '' })),
    );
    setBeacons((prev) =>
      prev.map((b) => (b.id === selectedId ? { ...b, institution_count: savedIds.length } : b)),
    );
  }, [savedIds]);

  // Cmd/Ctrl+Z undoes and Shift+Cmd/Ctrl+Z redoes while a beacon is edited, except in a field.
  useEffect(() => {
    if (!editingBeaconId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingBeaconId]);

  const loadBeacons = async () => {
    try {
      setIsLoading(true);
      // A team holds at most its NR_BEACONS__TOTAL (200 unless raised): two pages at most.
      setBeacons(await allPages(eduleadV4.beacons.list({ page_size: 100, order_by: 'name' })));
    } catch (error) {
      toastV4Error(error, 'Failed to load beacons');
      setBeacons([]);
    } finally {
      setIsLoading(false);
    }
  };

  // The selected beacons' colors on the map: each member in its beacon's, the first selected
  // beacon's where they share one; the beacon selected alone is the one in hand. Recomputed when
  // a beacon is recolored, from the cache.
  const publishFills = () => {
    const fills: Record<string, string> = {};
    selectedIds.forEach((id) => {
      const fill = beaconFillColor(beacons.find((b) => b.id === id)?.color);
      (beaconMembers.current.get(id) ?? []).forEach((m) => {
        if (!fills[m.id]) fills[m.id] = fill;
      });
    });
    setHighlightFills(fills);
    setBeaconFill(
      selectedId
        ? beaconFillColor(beacons.find((b) => b.id === selectedId)?.color)
        : DEFAULT_BEACON_FILL,
    );
  };
  useEffect(() => {
    if (shownOnMapKey === selectedKey && selectedKey !== '') publishFills();
  }, [beacons]);

  // The map shows the selected beacons' institutions; a beacon selected alone it edits against what
  // the API holds.
  useEffect(() => {
    setShownOnMapKey(null);
    if (selectedIds.length === 0) {
      setHighlighted([]);
      setHighlightFills({});
      setBeaconFill(DEFAULT_BEACON_FILL);
      setBeaconSavedIds(null);
      return;
    }
    let current = true;
    const highlight = async () => {
      try {
        const missing = selectedIds.filter((id) => !beaconMembers.current.has(id));
        if (missing.length > 0) setIsLoadingMembers(true);
        const read = await Promise.all(
          missing.map((beacon_id) =>
            allPages(eduleadV4.beacons.listInstitutions({ beacon_id, page_size: 100 })),
          ),
        );
        missing.forEach((id, i) =>
          beaconMembers.current.set(
            id,
            read[i].flatMap((m) => (m.id ? [{ id: m.id, name: m.name ?? '' }] : [])),
          ),
        );
        if (!current) return;
        const members = selectedIds.flatMap((id) => beaconMembers.current.get(id) ?? []);
        // The members' names, for the form's list: from the cache on every open, so a list never
        // depends on the read that filled it.
        const names: Record<string, string> = {};
        members.forEach((m) => {
          if (m.name) names[m.id] = m.name;
        });
        if (Object.keys(names).length > 0) rememberNames(names);
        const ids = [...new Set(members.map((m) => m.id))];
        setHighlighted(ids);
        publishFills();
        setBeaconSavedIds(selectedId ? ids : null);
        setShownOnMapKey(selectedKey);
      } catch (error) {
        toastV4Error(error, "Failed to load the beacons' institutions");
      } finally {
        if (current) setIsLoadingMembers(false);
      }
    };
    highlight();
    // Another selection replaces this highlight.
    return () => {
      current = false;
    };
  }, [selectedKey]);

  // Leaving Beacons mode takes the highlight, its colors and the edited beacon with it.
  useEffect(
    () => () => {
      setHighlighted([]);
      setHighlightFills({});
      setBeaconFill(DEFAULT_BEACON_FILL);
      setBeaconSavedIds(null);
      setEditingBeaconId(null);
    },
    [],
  );

  /**
   * Selects a beacon alone, or none with a click on the one selected; additive (Cmd/Ctrl+click) adds
   * it to the selection or takes it out. Edits not saved are dropped, after a word with the user.
   */
  const select = (id: string, isAdditive: boolean) => {
    if (useNavigatorStore.getState().isSavingBeacon) return;
    if (isDirty && !window.confirm('This beacon has unsaved changes. Discard them?')) return;
    const ids = isAdditive
      ? selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id]
      : selectedKey === id
        ? []
        : [id];
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (ids.length > 0) next.set(BEACON_PARAM, ids.join(','));
      else next.delete(BEACON_PARAM);
      next.delete(EDIT_PARAM);
      return next;
    });
  };

  const open = (id: string) => {
    setShownId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(BEACON_PARAM, id);
      next.set(EDIT_PARAM, '1');
      return next;
    });
  };

  /** Back from a beacon's form: the beacon stays selected, its edits staged. */
  const close = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(EDIT_PARAM);
      return next;
    });
  };

  /** Puts a write's returned beacon in place of the listed one, keeping the count the list shows. */
  const replaceBeacon = (beacon: Edulead.EduleadV4Beacon) => {
    setBeacons((prev) =>
      prev.map((b) =>
        b.id === beacon.id ? { ...beacon, institution_count: b.institution_count } : b,
      ),
    );
  };

  const shownBeacon = shownId ? (beacons.find((b) => b.id === shownId) ?? null) : null;

  return (
    <div className="navigator-panel navigator-beacons-panel">
      <div
        className={`navigator-beacons-panel__track ${openedId ? 'navigator-beacons-panel__track--shifted' : ''}`}
      >
        <div
          className="navigator-beacons-panel__pane navigator-beacons-panel__pane--list"
          aria-hidden={openedId !== null}
        >
          {/* What scrolls: the list, above the edited beacon's Save and Discard */}
          <div className="navigator-beacons-panel__list">
            <BeaconList
              beacons={beacons}
              isLoading={isLoading}
              onOpen={open}
              onSelect={select}
              selectedIds={selectedIds}
              onCreated={(beacon) =>
                setBeacons((prev) =>
                  [...prev, beacon].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
                )
              }
              onDeleted={(id) => {
                beaconMembers.current.delete(id);
                setBeacons((prev) => prev.filter((b) => b.id !== id));
              }}
            />
          </div>
        </div>
        <div
          className="navigator-beacons-panel__pane navigator-beacons-panel__pane--form"
          aria-hidden={openedId === null}
        >
          {shownBeacon?.id && (
            <BeaconDetail
              key={shownBeacon.id}
              beacon={shownBeacon}
              isOpen={openedId === shownBeacon.id}
              isLoadingMembers={isLoadingMembers}
              onBack={close}
              onChanged={replaceBeacon}
            />
          )}
        </div>
      </div>
    </div>
  );
}
